import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import SessionAutoJoin from "@/components/SessionAutoJoin";

/**
 * Mounts the SessionAutoJoin banner globally for logged-in clients so it
 * follows them across every page (not only /dashboard). Polls the user's
 * bookings every 20 s so a counselor-ready flip is picked up quickly.
 *
 * Counselors already have their own auto-join card on /counselor.
 * Admins (is_lead) are skipped — they don't take sessions themselves.
 */
export default function GlobalClientAutoJoin() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);

  // Any logged-in non-admin user gets the global banner: clients on
  // /bookings/mine, counselors on /bookings (assigned to them).
  const isEligible = user && !user.is_lead;
  const viewerRole = user?.role === "counselor" ? "counselor" : "client";
  const endpoint = viewerRole === "counselor" ? "/bookings" : "/bookings/mine";

  useEffect(() => {
    if (!isEligible) return undefined;
    let alive = true;

    const fetchOnce = async () => {
      try {
        const { data } = await api.get(endpoint);
        if (alive) setBookings(data || []);
      } catch (err) {
        if (err?.response?.status && err.response.status !== 401) {
          console.warn("Global auto-join bookings poll failed:", err?.message || err);
        }
      }
    };

    fetchOnce();
    const iv = setInterval(fetchOnce, 20_000);
    const onVis = () => { if (document.visibilityState === "visible") fetchOnce(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isEligible, endpoint]);

  if (!isEligible) return null;
  return <SessionAutoJoin bookings={bookings} viewerRole={viewerRole} />;
}
